import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Text,
  VStack,
  Container,
  Heading,
  Image,
  useBreakpointValue,
} from "@chakra-ui/react";
import { m } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  scrollBlockInViewConfig,
  useScrollBlockAnimController,
  useScrollDirection,
} from "../hooks/useScrollBlockAnim";
import { TEXTS } from "../constants/texts";
import TopA from "../images/TopANagasaki.jpg";
import TopB from "../images/TopBNagasaki.jpg";
// Hashed image manifest generated by scripts/optimize-images.mjs
// Note: CRA allows importing JSON without extra config
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import optimizedManifest from "../optimized/manifest.json";
import HaikeiA from "../images/Background/HaikeiA.jpg";
import RoyalAlbertHall from "../images/Assets/RoyalAlbertHall.jpg";
import PRmovieThumbnail2 from "../images/Thumbnail/PRmovieThumbnail2.jpg";
import { videoConfig } from "../config/videoConfig";
import { MotionDiv, MotionSpan } from "../lazyMotionDom";
import {
  royalAlbertHallSizes,
  trailerContentMaxW,
  trailerContentMaxWDesktopSx,
  trailerHeroSizes,
  trailerVideoMaxW,
  trailerVideoMaxWDesktopSx,
} from "../constants/trailerLayout";

const colors = {
  gold: "rgb(233, 203, 141)",
};

/**
 * 日本語本文の折り返し: break-all/anywhere は一文字だけの行を招きやすい。
 * strict で禁則に沿い、対応ブラウザでは pretty で行末の孤立文字を抑える。
 */
const trailerJapaneseWrapSx = {
  lineBreak: "strict",
  textWrap: "pretty",
} as const;

/** 下記本文 VStack の spacing={2}（0.5rem）に揃え、自動折り返しの行間を段落間に近づける */
const TRAILER_BODY_WRAP_LINE_EXTRA = "0.6rem";

/** false なら HLS 動画ブロックを非表示（マークアップとロジックは残す。再表示は true に） */
const TRAILER_VIDEO_VISIBLE = true;

const TRAILER_ANIM_KEYS = [
  "video",
  "text",
  "royalAlbert",
  "description",
] as const;

const trailerDesktopAvifSrcSet = (() => {
  try {
    const a = (optimizedManifest as any)["TopA"].avif;
    return `/optimized/TopA/${a["768"]} 768w, /optimized/TopA/${a["1280"]} 1280w, /optimized/TopA/${a["1920"]} 1920w`;
  } catch {
    return "/optimized/TopA/TopA-768.avif 768w, /optimized/TopA/TopA-1280.avif 1280w, /optimized/TopA/TopA-1920.avif 1920w";
  }
})();

const trailerDesktopWebpSrcSet = (() => {
  try {
    const a = (optimizedManifest as any)["TopA"].webp;
    return `/optimized/TopA/${a["768"]} 768w, /optimized/TopA/${a["1280"]} 1280w, /optimized/TopA/${a["1920"]} 1920w`;
  } catch {
    return "/optimized/TopA/TopA-768.webp 768w, /optimized/TopA/TopA-1280.webp 1280w, /optimized/TopA/TopA-1920.webp 1920w";
  }
})();

const trailerMobileAvifSrcSet = (() => {
  try {
    const b = (optimizedManifest as any)["TopB"].avif;
    return `/optimized/TopB/${b["480"]} 480w, /optimized/TopB/${b["768"]} 768w, /optimized/TopB/${b["1080"]} 1080w`;
  } catch {
    return "/optimized/TopB/TopB-480.avif 480w, /optimized/TopB/TopB-768.avif 768w, /optimized/TopB/TopB-1080.avif 1080w";
  }
})();

const trailerMobileWebpSrcSet = (() => {
  try {
    const b = (optimizedManifest as any)["TopB"].webp;
    return `/optimized/TopB/${b["480"]} 480w, /optimized/TopB/${b["768"]} 768w, /optimized/TopB/${b["1080"]} 1080w`;
  } catch {
    return "/optimized/TopB/TopB-480.webp 480w, /optimized/TopB/TopB-768.webp 768w, /optimized/TopB/TopB-1080.webp 1080w";
  }
})();

const trailerMainTexts = [
  TEXTS.CONTENT,
  TEXTS.MAIN_CONTENT_2,
  TEXTS.MAIN_CONTENT_3,
  TEXTS.MAIN_CONTENT_4,
  TEXTS.MAIN_CONTENT_5,
  TEXTS.MAIN_CONTENT_6,
  TEXTS.MAIN_CONTENT_7,
  TEXTS.MAIN_CONTENT_8,
  TEXTS.MAIN_CONTENT_9,
  TEXTS.MAIN_CONTENT_10,
  TEXTS.MAIN_CONTENT_11,
  TEXTS.MAIN_CONTENT_12,
] as const;

const preventDefault = (e: React.MouseEvent): void => {
  e.preventDefault();
};

type TrailerHlsBootstrapProps = {
  hlsVideoUrl: string;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  hlsInstanceRef: React.MutableRefObject<import("hls.js").default | null>;
};

/** HLS 初期化だけを分離。`TRAILER_VIDEO_VISIBLE` が false のときはマウントされず useEffect も走らない */
const TrailerHlsBootstrap: React.FC<TrailerHlsBootstrapProps> = ({
  hlsVideoUrl,
  videoRef,
  hlsInstanceRef,
}) => {
  useEffect(() => {
    let mounted = true;
    let localHls: import("hls.js").default | null = null;
    let videoElement: HTMLVideoElement | null = null;
    let retryRaf = 0;

    const onVideoLoadedMetadata = (e: Event) => {
      try {
        const el = e.currentTarget as HTMLVideoElement;
        el.volume = 0.5;
      } catch {}
    };
    const onVideoEnded = (e: Event) => {
      try {
        const el = e.currentTarget as HTMLVideoElement;
        el.pause();
        el.currentTime = 0;
      } catch {}
    };

    const cleanupMedia = () => {
      if (!videoElement) return;
      videoElement.removeEventListener(
        "loadedmetadata",
        onVideoLoadedMetadata as EventListener,
      );
      videoElement.removeEventListener("ended", onVideoEnded as EventListener);
      if (localHls) {
        try {
          localHls.destroy();
        } catch {}
        localHls = null;
      }
      if (hlsInstanceRef.current) {
        try {
          hlsInstanceRef.current.destroy();
        } catch {}
        hlsInstanceRef.current = null;
      }
      videoElement.pause();
      videoElement.removeAttribute("src");
      videoElement.load();
    };

    const init = async () => {
      if (!videoElement || !mounted) return;

      const canNativeHls = !!videoElement.canPlayType(
        "application/vnd.apple.mpegURL",
      );
      // iPhone のネイティブ HLS は問題なし。
      // Mac Safari はプレイリスト内の日本語パス解決で失敗しやすいので hls.js を使う。
      const ua = navigator.userAgent;
      const isIOS =
        /iPad|iPhone|iPod/i.test(ua) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
      const preferNativeHls = canNativeHls && isIOS;
      const encodedHlsUrl = encodeURI(hlsVideoUrl);

      videoElement.controls = true;
      videoElement.volume = 0.5;
      videoElement.preload = "metadata";

      if (preferNativeHls) {
        videoElement.src = encodedHlsUrl;
      } else {
        const hlsModule: any = await import("hls.js");
        if (!mounted || !videoElement) return;

        const Hls = hlsModule.default || hlsModule;
        if (Hls.isSupported()) {
          const hls = new Hls({
            enableWorker: true,
            lowLatencyMode: false,
            backBufferLength: 90,
          });
          localHls = hls;
          hls.loadSource(encodedHlsUrl);
          hls.attachMedia(videoElement);
          hlsInstanceRef.current = hls;
        } else if (canNativeHls) {
          videoElement.src = encodedHlsUrl;
        } else {
          console.warn("HLS not supported in this browser.");
          return;
        }
      }

      if (!mounted || !videoElement) return;

      videoElement.addEventListener("loadedmetadata", onVideoLoadedMetadata);
      videoElement.addEventListener("ended", onVideoEnded);
    };

    const start = () => {
      videoElement = videoRef.current;
      if (!videoElement) {
        retryRaf = requestAnimationFrame(start);
        return;
      }
      void init();
    };

    start();

    return () => {
      mounted = false;
      cancelAnimationFrame(retryRaf);
      cleanupMedia();
      videoElement = null;
    };
  }, [hlsVideoUrl, videoRef, hlsInstanceRef]);

  return null;
};

const Trailer: React.FC = () => {
  const { scrollDirRef, registerDirectionChange } = useScrollDirection({
    enableTouch: true,
    throttled: true,
  });
  const { animations, blockOnChange, resyncAll } = useScrollBlockAnimController(
    TRAILER_ANIM_KEYS,
    scrollDirRef,
    {
      isKeyEnabled: (key) => key !== "video" || TRAILER_VIDEO_VISIBLE,
      suppressBoundaryHide: (key) => key === "description",
    },
  );

  useEffect(
    () => registerDirectionChange(resyncAll),
    [registerDirectionChange, resyncAll],
  );

  const hlsVideoUrl = videoConfig.hlsVideoUrl;
  const trailerVideoRef = useRef<HTMLVideoElement>(null);
  const hlsInstanceRef = useRef<import("hls.js").default | null>(null);

  const { ref: componentRef } = useInView({
    triggerOnce: false,
    threshold: 0.05,
    rootMargin: "0px 0px 150px 0px",
  });

  const [heroPaddingTopPercent, setHeroPaddingTopPercent] =
    useState<string>("56.25%");

  const { ref: videoSectionRef } = useInView({
    ...scrollBlockInViewConfig,
    onChange: blockOnChange("video"),
  });
  const { ref: textRef } = useInView({
    ...scrollBlockInViewConfig,
    onChange: blockOnChange("text"),
  });
  const { ref: royalAlbertRef } = useInView({
    ...scrollBlockInViewConfig,
    onChange: blockOnChange("royalAlbert"),
  });
  const { ref: descriptionRef } = useInView({
    ...scrollBlockInViewConfig,
    onChange: blockOnChange("description"),
  });

  // 画像選択（レスポンシブ）
  const topImage = useBreakpointValue({
    base: TopB,
    md: TopA,
  });

  // HLSビデオのアニメーション設定
  const videoFadeVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        delay: 0.2,
      },
    },
    instant: {
      opacity: 1,
      transition: {
        duration: 0,
        ease: "easeOut",
      },
    },
  };

  const titleVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 1.0,
        ease: "easeOut",
        delay: 0.1,
      },
    },
    instant: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0,
        ease: "easeOut",
      },
    },
  };

  /** 本文は opacity のみ（y スライドは境目で重なって見えやすい） */
  const descriptionBodyVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 1.0, ease: "easeOut", delay: 0.2 },
    },
    instant: {
      opacity: 1,
      transition: { duration: 0 },
    },
  };

  return (
    <Box
      pos="relative"
      overflow="hidden"
      ref={componentRef}
      sx={{ willChange: "transform, opacity", backfaceVisibility: "hidden" }}
    >
      {/* トップ画像 */}
      <Box
        position="relative"
        w="100%"
        sx={{
          "&::before": {
            content: '""',
            display: "block",
            paddingTop: heroPaddingTopPercent, // 実画像の比率に追従
          },
        }}
      >
        <picture>
          {/* Desktop sources (md and up) */}
          <source
            media="(min-width: 48em)"
            type="image/avif"
            srcSet={trailerDesktopAvifSrcSet}
            sizes="100vw"
          />
          <source
            media="(min-width: 48em)"
            type="image/webp"
            srcSet={trailerDesktopWebpSrcSet}
            sizes="100vw"
          />
          {/* Mobile sources (below md) */}
          <source
            media="(max-width: 47.99em)"
            type="image/avif"
            srcSet={trailerMobileAvifSrcSet}
            sizes="100vw"
          />
          <source
            media="(max-width: 47.99em)"
            type="image/webp"
            srcSet={trailerMobileWebpSrcSet}
            sizes="100vw"
          />
          <img
            src={topImage as string}
            alt="top main"
            loading="eager"
            decoding="async"
            fetchPriority="high"
            className="tg-abs-cover-img"
            onLoad={(e) => {
              const img = e.currentTarget as HTMLImageElement;
              if (img.naturalWidth && img.naturalHeight) {
                const ratio = (img.naturalHeight / img.naturalWidth) * 100;
                setHeroPaddingTopPercent(`${ratio}%`);
              }
            }}
            onContextMenu={preventDefault}
            draggable={false}
          />
        </picture>
      </Box>

      {/* メインコンテンツエリア */}
      <Box position="relative">
        {/* 背景画像 - 固定高さを削除して、コンテンツに合わせて自動的に高さを調整 */}
        <Box position="relative">
          <picture>
            <source
              type="image/avif"
              srcSet="/optimized/HaikeiA/HaikeiA-768.avif 768w, /optimized/HaikeiA/HaikeiA-1280.avif 1280w, /optimized/HaikeiA/HaikeiA-1920.avif 1920w"
              sizes={trailerHeroSizes}
            />
            <source
              type="image/webp"
              srcSet="/optimized/HaikeiA/HaikeiA-768.webp 768w, /optimized/HaikeiA/HaikeiA-1280.webp 1280w, /optimized/HaikeiA/HaikeiA-1920.webp 1920w"
              sizes={trailerHeroSizes}
            />
            <Image
              src={HaikeiA}
              alt="Background"
              w="100%"
              objectFit="cover"
              objectPosition="center bottom"
              position="absolute"
              top="0"
              left="0"
              right="0"
              bottom="0"
              minHeight="100%"
              zIndex="-1"
            />
          </picture>

          <Box
            width="100%"
            px={{
              base: "5%",
              sm: "8%",
              md: "10%",
              lg: "12%",
            }}
            py={{
              base: "40px",
              sm: "50px",
              md: "60px",
              lg: "80px",
            }}
            id="trailer-title" // HLS動画が再度有効になった場合は、trailer-video要素に戻す
          >
            <VStack
              spacing={{
                base: "30px",
                sm: "40px",
                md: "50px",
                lg: "60px",
              }}
              align="center"
            >
              {/* HLS動画 */}
              <MotionDiv
                ref={videoSectionRef}
                initial="hidden"
                animate={animations.video}
                variants={videoFadeVariants}
                className={
                  TRAILER_VIDEO_VISIBLE
                    ? "tg-trailer-video-slot"
                    : "tg-trailer-video-slot tg-trailer-video-slot--hidden"
                }
              >
                <Box
                  w="100%"
                  maxW={trailerVideoMaxW}
                  mx="auto"
                  position="relative"
                  borderRadius={{
                    base: "10px",
                    sm: "15px",
                    md: "20px",
                    lg: "25px",
                  }}
                  overflow="hidden"
                  sx={{
                    ...trailerVideoMaxWDesktopSx,
                    "&::before": {
                      content: '""',
                      display: "block",
                      paddingTop: "56.25%", // 16:9のアスペクト比
                    },
                  }}
                >
                  <video
                    ref={trailerVideoRef}
                    id="trailer-video"
                    className="tg-trailer-video-el"
                    controls
                    preload="metadata"
                    playsInline
                    poster={PRmovieThumbnail2}
                  ></video>
                  {TRAILER_VIDEO_VISIBLE && (
                    <TrailerHlsBootstrap
                      hlsVideoUrl={hlsVideoUrl}
                      videoRef={trailerVideoRef}
                      hlsInstanceRef={hlsInstanceRef}
                    />
                  )}
                </Box>
              </MotionDiv>

              {/* テキストセクション */}
              <MotionDiv
                ref={textRef}
                initial="hidden"
                animate={animations.text}
                variants={titleVariants}
                className="tg-w-full"
              >
                <VStack
                  spacing={{
                    base: 3,
                    sm: 4,
                    md: 5,
                  }}
                  width="100%"
                  px={{
                    base: 0,
                    sm: 1,
                  }}
                  mx="auto"
                  maxW={trailerContentMaxW}
                  sx={trailerContentMaxWDesktopSx}
                >
                  <m.div
                    key={`title-1`}
                    initial="hidden"
                    animate={animations.text}
                    variants={titleVariants}
                  >
                    <Text
                      color="white"
                      fontSize={{
                        base: "14px",
                        sm: "16px",
                        md: "24px",
                        lg: "27px",
                        xl: "32px",
                        "2xl": "40px",
                      }}
                      textAlign="center"
                      fontWeight="bold"
                      mb={{
                        base: "-10px",
                        sm: "-15px",
                        md: "-15px",
                        lg: "-15px",
                        xl: "-15px",
                        "2xl": "-15px",
                      }}
                      wordBreak="keep-all"
                      overflowWrap="break-word"
                      sx={trailerJapaneseWrapSx}
                    >
                      {TEXTS.TOPGUN_TITLE}
                    </Text>
                  </m.div>

                  <m.div
                    key={`title-2`}
                    initial="hidden"
                    animate={animations.text}
                    variants={titleVariants}
                  >
                    <Text
                      color="white"
                      fontSize={{
                        base: "14px",
                        sm: "16px",
                        md: "24px",
                        lg: "27px",
                        xl: "32px",
                        "2xl": "40px",
                      }}
                      textAlign="center"
                      fontWeight="bold"
                      mb={{
                        base: "-10px",
                        sm: "-15px",
                        md: "-15px",
                        lg: "-15px",
                        xl: "-15px",
                        "2xl": "-15px",
                      }}
                      wordBreak="keep-all"
                      overflowWrap="break-word"
                      sx={trailerJapaneseWrapSx}
                    >
                      {TEXTS.TOPGUN_TITLE_2}
                    </Text>
                  </m.div>

                  <m.div
                    key={`title-3`}
                    initial="hidden"
                    animate={animations.text}
                    variants={titleVariants}
                  >
                    <Text
                      color="white"
                      fontSize={{
                        base: "14px",
                        sm: "16px",
                        md: "24px",
                        lg: "27px",
                        xl: "32px",
                        "2xl": "40px",
                      }}
                      textAlign="center"
                      fontWeight="bold"
                      mb={{
                        base: 4,
                        sm: 5,
                        md: 6,
                        lg: 8,
                      }}
                      wordBreak="keep-all"
                      overflowWrap="break-word"
                      sx={trailerJapaneseWrapSx}
                    >
                      {TEXTS.TOPGUN_TITLE_3}
                    </Text>
                  </m.div>

                  {/* 残りのテキスト（明示的な配列で重複防止） */}
                  <VStack spacing={2} width="100%">
                    {trailerMainTexts.map((content, i) => (
                      <m.div
                        key={content}
                        initial="hidden"
                        animate={animations.text}
                        variants={{
                          hidden: { opacity: 0, y: 20 },
                          visible: {
                            opacity: 1,
                            y: 0,
                            transition: {
                              duration: 1.0,
                              delay: 0.1 + i * 0.1,
                            },
                          },
                          instant: {
                            opacity: 1,
                            y: 0,
                            transition: { duration: 0 },
                          },
                        }}
                      >
                        <Text
                          color="white"
                          fontSize={{
                            base: "10px",
                            sm: "11px",
                            md: "15px",
                            lg: "16px",
                            xl: "20px",
                            "2xl": "22px",
                          }}
                          textAlign="center"
                          lineHeight={`calc(1em + ${TRAILER_BODY_WRAP_LINE_EXTRA})`}
                          width="100%"
                          wordBreak="keep-all"
                          overflowWrap="break-word"
                          sx={trailerJapaneseWrapSx}
                          whiteSpace={{ base: "pre-line", xl: "normal" }}
                          letterSpacing={{ base: "-0.01em", sm: "normal" }}
                          mb={
                            i === 3 || i === 6 || i === 9
                              ? { base: 3, sm: 3, md: 4, lg: 5, xl: 6 }
                              : undefined
                          }
                          px={{ base: "1", sm: "0" }}
                        >
                          {content}
                        </Text>
                      </m.div>
                    ))}
                  </VStack>
                </VStack>
              </MotionDiv>

              {/* ロイヤルアルバートホール画像 */}
              <Box width="100%" textAlign="center">
                <Box
                  w="100%"
                  maxW={trailerContentMaxW}
                  height="auto"
                  overflow="hidden"
                  mx="auto"
                  display="block"
                  ref={royalAlbertRef}
                  sx={trailerContentMaxWDesktopSx}
                >
                  <MotionDiv
                    className="tg-flex-center-fill"
                    initial="hidden"
                    animate={animations.royalAlbert}
                    variants={{
                      hidden: {
                        opacity: 0,
                        scale: 1.2,
                      },
                      visible: {
                        opacity: 1,
                        scale: 1,
                        transition: {
                          duration: 1.5,
                          ease: "easeOut",
                        },
                      },
                      instant: {
                        opacity: 1,
                        scale: 1,
                        transition: {
                          duration: 0,
                        },
                      },
                    }}
                  >
                    <picture className="tg-picture-block">
                      <source
                        type="image/avif"
                        srcSet="/optimized/RoyalAlbertHall/RoyalAlbertHall-600.avif 600w, /optimized/RoyalAlbertHall/RoyalAlbertHall-900.avif 900w, /optimized/RoyalAlbertHall/RoyalAlbertHall-1200.avif 1200w"
                        sizes={royalAlbertHallSizes}
                      />
                      <source
                        type="image/webp"
                        srcSet="/optimized/RoyalAlbertHall/RoyalAlbertHall-600.webp 600w, /optimized/RoyalAlbertHall/RoyalAlbertHall-900.webp 900w, /optimized/RoyalAlbertHall/RoyalAlbertHall-1200.webp 1200w"
                        sizes={royalAlbertHallSizes}
                      />
                      <img
                        src={RoyalAlbertHall as string}
                        alt="RoyalAlbertHall"
                        loading="eager"
                        decoding="async"
                        className="tg-img-fluid-block"
                        onContextMenu={preventDefault}
                        draggable={false}
                      />
                    </picture>
                  </MotionDiv>
                </Box>
              </Box>

              {/* 説明セクション（親は IO 用ラッパーのみ。子要素だけアニメーション＝他テキストと同構造） */}
              <Box ref={descriptionRef} className="tg-w-full">
                <Container
                  maxW={trailerContentMaxW}
                  sx={trailerContentMaxWDesktopSx}
                  mb={{
                    base: "20px",
                    sm: "30px",
                    md: "40px",
                    lg: "50px",
                  }}
                  px={{
                    base: "2",
                    sm: undefined,
                    md: undefined,
                  }}
                >
                  <Box
                    borderColor={colors.gold}
                    borderLeft={`2px solid ${colors.gold}`}
                    borderRight={`2px solid ${colors.gold}`}
                    borderBottom={`2px solid ${colors.gold}`}
                    maxW="100%"
                    overflow="visible"
                    p={{
                      base: 3,
                      sm: 5,
                      md: 6,
                      lg: 8,
                    }}
                    pos="relative"
                    pt={{
                      base: "30px",
                      sm: "35px",
                      md: "40px",
                      lg: "50px",
                    }}
                    sx={{
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: {
                          base: 0,
                          sm: 0,
                          md: 0,
                          lg: 0,
                        },
                        width: {
                          base: "30%",
                          sm: "30%",
                          md: "30%",
                          lg: "30%",
                        },
                        height: "2px",
                        background: colors.gold,
                      },
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        right: {
                          base: 0,
                          sm: 0,
                          md: 0,
                          lg: 0,
                        },
                        width: {
                          base: "30%",
                          sm: "30%",
                          md: "30%",
                          lg: "30%",
                        },
                        height: "2px",
                        background: colors.gold,
                      },
                    }}
                  >
                    <Heading
                      pos="absolute"
                      top="0"
                      left="50%"
                      transform="translate(-50%, -50%)"
                      px={{
                        base: 2,
                        sm: 3,
                        md: 4,
                      }}
                      py={{
                        base: 1,
                        sm: 1.5,
                        md: 2,
                      }}
                      color="white"
                      fontSize={{
                        base: "13px",
                        sm: "15px",
                        md: "20px",
                        lg: "30px",
                        xl: "32px",
                        "2xl": "36px",
                      }}
                      whiteSpace={{
                        base: "normal",
                        md: "nowrap",
                      }}
                      textAlign="center"
                      minWidth="fit-content"
                    >
                      <MotionSpan
                        initial="hidden"
                        animate={animations.description}
                        variants={titleVariants}
                        className="tg-inline-block"
                      >
                        {TEXTS.Description.title}
                        <Text
                          as="span"
                          fontSize={{
                            base: "8px",
                            sm: "8px",
                            md: "8px",
                            lg: "21px",
                            xl: "20px",
                            "2xl": "20px",
                          }}
                          position="relative"
                          top={{
                            base: "-5px",
                            sm: "-6px",
                            md: "-8px",
                            lg: "-10px",
                            "2xl": "-12px",
                          }}
                        >
                          {TEXTS.Description.title_2}
                        </Text>
                        {TEXTS.Description.title_3}
                      </MotionSpan>
                    </Heading>
                    <MotionDiv
                      initial="hidden"
                      animate={animations.description}
                      variants={descriptionBodyVariants}
                      className="tg-w-full"
                      style={{
                        minWidth: 0,
                        maxWidth: "100%",
                        overflowX: "clip",
                      }}
                    >
                      <VStack spacing={0} w="100%" align="stretch">
                        {TEXTS.Description.content.map((text, index) => (
                          <Text
                            key={text}
                            w="100%"
                            minW={0}
                            maxW="100%"
                            color="white"
                            fontSize={{
                              base: index === 2 ? "8px" : "11px",
                              sm: index === 2 ? "10px" : "11px",
                              md: index === 2 ? "12px" : "15px",
                              lg: index === 2 ? "14px" : "18px",
                              xl: index === 2 ? "16px" : "20px",
                              "2xl": index === 2 ? "16px" : "22px",
                            }}
                            textAlign="center"
                            mt={
                              index === 2
                                ? {
                                    base: 3,
                                    sm: 4,
                                    md: 5,
                                    lg: 6,
                                  }
                                : 0
                            }
                            whiteSpace="pre-line"
                            wordBreak="keep-all"
                            overflowWrap="break-word"
                            sx={trailerJapaneseWrapSx}
                            lineHeight={{
                              base: "1.5",
                              sm: undefined,
                              md: undefined,
                            }}
                            letterSpacing={{
                              base: "-0.01em",
                              sm: undefined,
                            }}
                          >
                            {text}
                          </Text>
                        ))}
                      </VStack>
                    </MotionDiv>
                  </Box>
                </Container>
              </Box>
            </VStack>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Trailer;
